/**
 * deformCompare.js — деформируемое уточнение связи двух исследований в режиме
 * сравнения. Обвязка над чистым ядром core/deformReg.js (демоны, проверены
 * фантомом в tests/deform_test.mjs); здесь — только достать тома, привести к
 * общей сетке и отдать перенос точек синхронизации окон.
 *
 * НАЗНАЧЕНИЕ ПОЛЯ — НАВИГАЦИЯ: синхронная прокрутка по анатомии и перенос
 * точки очага между датами. Поле НЕ деформирует показываемые изображения и НЕ
 * годится для измерений по искривлённой картинке — об этом сказано в тосте и
 * в подписи полоски.
 *
 * ПОРЯДОК. Если врач уже нажимал «Совместить» (жёсткая регистрация по взаимной
 * информации, rtRegisterModule), деформация считается ПОВЕРХ неё и снимает
 * остаток: дыхание, наполнение, изгиб. Без совмещения поле включит и укладку —
 * это работает, но честно проговаривается: крупный сдвиг стола лучше снять
 * жёстким совмещением, демоны для него — молоток не по гвоздю.
 *
 * СЕТКА. Опорный том прореживается до ≤ 72 вокселей по каждой оси (шаг общий,
 * extractVolume), подвижный ресэмплируется НА ЭТУ СЕТКУ по мировым координатам
 * (с учётом жёсткой матрицы, если есть). Поле хранится на этой сетке; перенос
 * точки: мир → индекс сетки → +u → мир (→ жёсткая матрица).
 */

import { getRenderingEngine, cornerstone } from '../core/viewportState.js';
import { getComparePair, getCompareViewportIds } from '../core/compareState.js';
import { setCompareTransform, enableCompareSync } from '../core/viewportSync.js';
import { worldToIdx, idxToWorld, sampleTrilinear } from './rigidRegister.js';
import { демоны3д, точкаПоПолю, точкаПоПолюНазад, сводкаПоля } from '../core/deformReg.js';
// extractVolume живёт в ОБВЯЗКЕ (rtRegisterModule), не в чистом ядре
// rigidRegister — ему нужен доступ к тому Cornerstone. Поймано link_check.
import { currentMatrix, extractVolume } from './rtRegisterModule.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

let _поле = null;   // { поле:{ux,uy,uz,dims}, сетка:{dims,spacing,origin,direction}, T, Tобр, сводка }

const _volById = (id) => { try { return cornerstone?.cache?.getVolume?.(id) || null; } catch (e) { return null; } };

async function _томСерии(seriesUID) {
  const полное = `cornerstoneStreamingImageVolume:volume_${seriesUID}`;
  let cs = _volById(полное) || _volById(`volume_${seriesUID}`);
  if (!cs) {
    try {
      const { ensureVolumeReady } = await import('../core/viewport.js');
      const { getWorkerSeriesImageIds } = await import('../loaders/loaderWorkerBridge.js');
      let кадры = getWorkerSeriesImageIds(seriesUID) || [];
      if (!кадры.length) {
        const лм = await import('../loaders/loaderManager.js');
        кадры = лм.getSeriesImageIds?.(seriesUID) || [];
      }
      if (!кадры.length) return null;
      const id = await ensureVolumeReady(`volume_${seriesUID}`, кадры);
      cs = id ? _volById(id) : null;
    } catch (e) { log.warn('[Деформация] том:', e.message); return null; }
  }
  return cs;
}

/** Обратная к жёсткой матрице 4×4 (строчная запись [16]): R|t → Rᵀ|−Rᵀt. */
function _обратнаяЖёсткой(T) {
  if (!T) return null;
  const R = [T[0], T[1], T[2], T[4], T[5], T[6], T[8], T[9], T[10]];
  const t = [T[3], T[7], T[11]];
  const о = new Array(16).fill(0); о[15] = 1;
  о[0] = R[0]; о[1] = R[3]; о[2] = R[6];
  о[4] = R[1]; о[5] = R[4]; о[6] = R[7];
  о[8] = R[2]; о[9] = R[5]; о[10] = R[8];
  о[3] = -(о[0] * t[0] + о[1] * t[1] + о[2] * t[2]);
  о[7] = -(о[4] * t[0] + о[5] * t[1] + о[6] * t[2]);
  о[11] = -(о[8] * t[0] + о[9] * t[1] + о[10] * t[2]);
  return о;
}
const _прим = (T, p) => (T ? [
  T[0] * p[0] + T[1] * p[1] + T[2] * p[2] + T[3],
  T[4] * p[0] + T[5] * p[1] + T[6] * p[2] + T[7],
  T[8] * p[0] + T[9] * p[1] + T[10] * p[2] + T[11],
] : p);

/**
 * Посчитать деформируемое уточнение для текущей пары сравнения и включить его
 * в перенос точек. Опорное — ПЕРВОЕ окно (как и в жёстком совмещении).
 */
export async function уточнитьДеформацией(o = {}) {
  const пара = getComparePair();
  const окна = getCompareViewportIds();
  if (!пара?.fixedSeriesUID || !пара?.movingSeriesUID || окна.length < 2) {
    return { ok: false, причина: 'нет пары исследований в окнах сравнения' };
  }
  const csF = await _томСерии(пара.fixedSeriesUID);
  const csM = await _томСерии(пара.movingSeriesUID);
  if (!csF || !csM) return { ok: false, причина: 'тома пары не собрались' };

  // Жёсткая матрица, если совмещение уже сделано: точка ОПОРНОГО → ПОДВИЖНЫЙ.
  let T = null;
  try { T = currentMatrix?.() || null; } catch (e) { T = null; }
  const Tобр = _обратнаяЖёсткой(T);

  // Сетка деформации: опорный, прорежённый до ≤ 72 по каждой оси.
  const исходные = extractVolume(csF, 1, { noCopy: true });
  if (!исходные) return { ok: false, причина: 'у опорного тома нет вокселей' };
  const шаг = Math.max(1, Math.ceil(Math.max(...исходные.dims) / (o.сетка ?? 72)));
  const fixed = extractVolume(csF, шаг);
  const движОригинал = extractVolume(csM, 1, { noCopy: true });
  if (!fixed || !движОригинал) return { ok: false, причина: 'тома не читаются' };

  // Подвижный — на сетку опорного, по мировым координатам (через T, если есть).
  const [nx, ny, nz] = fixed.dims;
  const m = new Float32Array(nx * ny * nz);
  let попало = 0;
  for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const мир = idxToWorld(fixed, i, j, k);
    const идx = worldToIdx(движОригинал, _прим(T, мир));
    const v = sampleTrilinear(движОригинал, идx, NaN);
    const п = i + j * nx + k * nx * ny;
    if (Number.isNaN(v)) { m[п] = fixed.data[п]; continue; }   // вне поля силы нет: разница 0
    m[п] = v; попало++;
  }
  const перекрытие = попало / (nx * ny * nz);
  if (перекрытие < 0.25) {
    return { ok: false, причина: `тома пересеклись лишь на ${(перекрытие * 100).toFixed(0)}% — сначала «Совместить» (жёстко), деформация ловит остаток, а не укладку в десятки сантиметров` };
  }

  const р = демоны3д({ данные: Float32Array.from(fixed.data), dims: fixed.dims },
    { данные: m, dims: fixed.dims },
    { уровней: 3, итераций: [50, 35, 20] });
  if (!р.ok) return { ok: false, причина: р.причина };
  const сводка = сводкаПоля(р, fixed.spacing);

  _поле = { поле: р, сетка: fixed, T, Tобр, сводка, перекрытие };

  // Перенос точек: прежний контракт setCompareTransform (первое окно — опорное).
  enableCompareSync(окна);
  setCompareTransform((точка, откуда, куда, порядок) => {
    const прямое = Array.isArray(порядок) ? порядок[0] === откуда : true;
    const с = _поле;
    if (!с) return точка;
    if (прямое) {
      const x = worldToIdx(с.сетка, точка);
      const x2 = точкаПоПолю(с.поле, x);
      return _прим(с.T, idxToWorld(с.сетка, x2[0], x2[1], x2[2]));
    }
    const q = _прим(с.Tобр, точка);
    const y = worldToIdx(с.сетка, q);
    const x = точкаПоПолюНазад(с.поле, y);
    return idxToWorld(с.сетка, x[0], x[1], x[2]);
  });

  log.info(`[Деформация] поле готово: среднее ${сводка.среднееМм.toFixed(1)} мм, макс ${сводка.максМм.toFixed(1)} мм, `
    + `MSE ${р.mse0.toFixed(0)}→${р.mse1.toFixed(0)}, сетка ${fixed.dims.join('×')} (шаг ${шаг}), `
    + `жёсткое совмещение ${T ? 'учтено' : 'НЕ делалось — поле включает укладку'}`);
  return { ok: true, сводка, жёсткоеУчтено: !!T, улучшение: р.улучшение, сетка: fixed.dims, шаг };
}

/** Снять деформируемый перенос (жёсткое совмещение, если было, остаётся своим путём). */
export function снятьДеформацию() {
  _поле = null;
  setCompareTransform(null);
  return true;
}

export const деформацияАктивна = () => !!_поле;
export const сводкаДеформации = () => (_поле ? { ..._поле.сводка, перекрытие: _поле.перекрытие, жёсткоеУчтено: !!_поле.T } : null);
